import { createBrowserClient } from "@supabase/ssr";
import type { SupabaseClient } from "@supabase/supabase-js";
import { withBase } from "./base";
import type { AppUser } from "../types";

export type PublicConfig = {
  supabaseUrl: string;
  supabasePublishableKey: string;
  authEnabled: boolean;
  /** Whether "Continue with Google" is offered. The Worker's switch, so it can go off without a rebuild. */
  googleSignIn: boolean;
};

let client: SupabaseClient | null = null;
let googleSignIn = false;

export async function loadPublicConfig(): Promise<PublicConfig> {
  const fallback: PublicConfig = { supabaseUrl: "", supabasePublishableKey: "", authEnabled: false, googleSignIn: false };
  try {
    const response = await fetch(withBase("/api/config"));
    if (!response.ok) return fallback;
    const config = (await response.json()) as Partial<PublicConfig>;
    return {
      supabaseUrl: typeof config.supabaseUrl === "string" ? config.supabaseUrl : "",
      supabasePublishableKey:
        typeof config.supabasePublishableKey === "string" ? config.supabasePublishableKey : "",
      authEnabled: Boolean(config.authEnabled),
      googleSignIn: config.googleSignIn === true,
    };
  } catch {
    // In local Vite dev (no Worker) /api/config returns the SPA shell, not JSON.
    // Fall back to signed-out instead of throwing an unhandled rejection.
    return fallback;
  }
}

/** Read by the sign-in card; set once the config has been loaded. */
export function googleSignInEnabled() {
  return googleSignIn;
}

/**
 * Sign-in is owned by Al Noor Tuition, not by this app.
 *
 * `createBrowserClient` (@supabase/ssr) keeps the session in cookies under the
 * same `sb-<project-ref>-auth-token` key the LMS writes from the server. Because
 * this app is served from a path on the same origin
 * (www.alnoortuition.com/homeschool), a parent who is signed in to the LMS is
 * already signed in here — and signing out here signs them out everywhere.
 * That is deliberate: one account, one session, no second password database.
 */
export function configureAuth(config: PublicConfig) {
  if (!config.authEnabled) return null;
  googleSignIn = config.googleSignIn;
  client ??= createBrowserClient(config.supabaseUrl, config.supabasePublishableKey);
  return client;
}

export function authClient() {
  return client;
}

/** Where a signed-out visitor goes, returning here afterwards. */
export function loginUrl() {
  const next = window.location.pathname + window.location.search;
  return `/lms/login?next=${encodeURIComponent(next)}`;
}

/**
 * The verified Al Noor identity for the current session.
 *
 * Deliberately a round trip to the Worker rather than a read of the JWT: the
 * role decides whether the admin screens appear, and the token's copy of it
 * could have been issued before a demotion. The Worker re-reads `profiles`.
 */
export async function fetchAppUser(accessToken: string): Promise<AppUser | null> {
  const response = await fetch(withBase("/api/me"), {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) return null;
  return (await response.json()) as AppUser;
}

/**
 * Make sure this account has its Al Noor parent profile.
 *
 * ── Why the app has to ask ──────────────────────────────────────────────────
 * Every Al Noor screen — /api/me included — treats an account with no
 * `profiles` row as signed out, and only the service role can write one. The
 * homeschool Worker deliberately holds no such key, so the main site does it:
 * POST /api/homeschool/profile, which proves the caller from their own token,
 * creates the row only if it is missing, and only ever as a parent.
 *
 * Supabase signs a new family in the moment they sign up — "Confirm email" is
 * off on the project — so this is called straight after sign-up, and again
 * whenever a session turns up with no profile. The second is what let the
 * families who joined before this existed in: they had been signed in all
 * along, to an account nothing had finished creating.
 *
 * On the main site's own path, never under /homeschool: it is that Worker's
 * route. Never throws — a failure here just leaves the person signed out,
 * which is what would have happened anyway.
 */
export async function ensureParentProfile(accessToken: string): Promise<boolean> {
  try {
    const response = await fetch(`${window.location.origin}/api/homeschool/profile`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * The verified identity for a token, finishing an account that never got its
 * profile on the way. `null` when the token is no good, or the account is held.
 */
export async function resolveAppUser(accessToken: string): Promise<AppUser | null> {
  const found = await fetchAppUser(accessToken);
  if (found) return found;
  if (!(await ensureParentProfile(accessToken))) return null;
  return fetchAppUser(accessToken);
}

/** The cookie the main site's /auth/callback reads once — see lib/oauth-intent.ts there. */
const OAUTH_INTENT_COOKIE = "alnoor_oauth_intent";
const OAUTH_INTENT_MAX_AGE = 60 * 10;

/**
 * "Continue with Google".
 *
 * ── The two rules this has to keep ──────────────────────────────────────────
 * 1. The redirect is EXACTLY https://www.alnoortuition.com/auth/callback. That
 *    is the only URL on Supabase's allow-list, and the list matches it whole:
 *    with any path or query added, Supabase quietly sends the visitor to the
 *    apex home page instead, holding a code nothing will exchange, and they
 *    land signed out. Probed against the live project on 2026-09-26.
 * 2. So where to come back to, and that a new account is a parent, travel in
 *    the short-lived intent cookie the callback reads — the same one the LMS's
 *    own Google button sets. The PKCE verifier rides the same way: the browser
 *    client keeps it in a cookie on this origin, which the callback can read.
 *
 * The callback creates the parent profile for a first-time Google account,
 * signs them in on this origin, and sends them back here. A cancelled or
 * refused sign-in comes back as /homeschool/?error=oauth.
 */
export async function startGoogleSignIn(supabase: SupabaseClient): Promise<{ ok: true } | { ok: false; error: string }> {
  const intent = { next: withBase("/"), role: "parent", from: "lms" };
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie =
    `${OAUTH_INTENT_COOKIE}=${encodeURIComponent(JSON.stringify(intent))}; Path=/; ` +
    `Max-Age=${OAUTH_INTENT_MAX_AGE}; SameSite=Lax${secure}`;

  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    // A shared family tablet is common; let them pick which Google account.
    options: { redirectTo: `${window.location.origin}/auth/callback`, queryParams: { prompt: "select_account" } },
  });
  return error ? { ok: false, error: error.message } : { ok: true };
}

/**
 * When this session was opened by a password-reset link, if it was.
 *
 * The link is verified by the main site's /auth/confirm, on the server, so the
 * browser client never sees the PASSWORD_RECOVERY event it would raise for a
 * link it handled itself. The token still says how it was issued: Supabase
 * records the method in the `amr` claim, and a reset link's is "recovery".
 * Returns the time it was issued (seconds), so a prompt can be shown once per
 * reset rather than once per page load.
 */
export function recoveryIssuedAt(accessToken: string, now = Date.now()): number | null {
  try {
    const payload = accessToken.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded)) as { amr?: Array<{ method?: string; timestamp?: number }> };
    const recovery = (claims.amr ?? []).find((entry) => entry.method === "recovery");
    if (!recovery?.timestamp) return null;
    // A reset link is good for an hour; after that the prompt is stale news.
    return now / 1000 - recovery.timestamp < 60 * 60 ? recovery.timestamp : null;
  } catch {
    return null;
  }
}
